// eslint-disable-next-line no-unused-vars
import React, { forwardRef } from 'react';
import styled from '@emotion/styled';
import { shape, string, func, bool } from 'prop-types';
import { UserRound, ChevronUp, AtSign, Phone, Copy } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import copyToClipboard from 'Utils/copyToClipboard';
import useAnalytics from 'Hooks/useAnalytics';
import useToastNotifications from 'Hooks/useToastNotifications';
import Alerts from 'Containers/Alerts/Alerts';
import Img from 'Containers/Image/Image';

const StyledAccountManagerWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const StyledAccountManager = styled.div`
  display: flex;
  align-items: center;
  min-width: 165px;
  color: ${props => props.theme.colors.darkGray};
  font-size: 0.875rem;

  .copy {
    margin-left: 8px;
    cursor: pointer;
    color: ${props => props.theme.colors.altOrange};
  }

  .preview {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 11px;
    border-radius: 40px;
    width: 40px;
    height: 40px;

    > * {
      margin: 0 auto;
    }

    img {
      display: block;
      height: 100%;
      border-radius: 40px;
    }
  }

  .details p,
  .details a {
    font-size: 0.875rem;
    line-height: 1.25em;
  }

  .name a {
    color: inherit;
  }

  .title {
    color: ${props => props.theme.colors.gray};
  }

  .phone a {
    color: ${props => props.theme.colors.default};
  }

  .email {
    text-transform: lowercase;
  }

  .tooltip-content {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 0.875rem;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 14px);
  right: 0;
  width: 145%;
  color: ${props => props.theme.colors.darkBlue};
  background-color: ${props => props.theme.colors.white};
  border: 1px solid ${props => props.theme.products.colors.border};
  z-index: 13;
  box-shadow: 0 0 40px #00000029;
  opacity: ${props => props.$open ? 1 : 0};
  visibility: ${props => props.$open ? 'visible' : 'hidden'};
  transition: opacity 0.25s ease-out;

  &:after {
    display: block;
    position: absolute;
    top: -2px;
    right: 10px;
    width: 10px;
    height: 10px;
    background-color: #FFF;
    transform: rotate(45deg) translateX(-50%);
    content: '';
  }

  ul {
    padding: 0 12px;

    li {
      display: flex;
      align-items: center;
      font-size: 0.875rem;
      padding: 10px 0;
      color: ${props => props.theme.colors.darkGray};
      line-height: 1rem;
      border-bottom: 1px solid ${props => props.theme.colors.shadowGray};

      span {
        margin-left: 5px;
      }

      svg {
        margin-right: 5px;
      }
    }
  }
`;

const StyledChevronUp = styled(ChevronUp)`
  color: inherit;
  transform: scale(0.9) ${props => props.$menuOpen ? '' : 'rotate(180deg)'};
  margin-left: 8px;
  cursor: pointer;
`;

const AccountManagerComponent = forwardRef(({
  accountManager,
  accountManagerLabel,
  onToggle,
  menuOpen,
  isHeader,
}, ref) => {
  const toast = useToastNotifications();
  const { t } = useTranslation();
  const {
    firstName = '',
    lastName = '',
    email = '',
    phone = '',
    profilePicture
  } = accountManager || {};
  const fullName = `${firstName} ${lastName}`.trim();
  const analytics = useAnalytics();

  const copyCodeToClipboard = async (code, info) => {
    const resp = await copyToClipboard(code);
    analytics.track(info);

    if (resp) {
      toast.addNotification({
        type: 'success',
        title: t('products.copyCodeSuccess')
      });
    }
  };

  return (
    <StyledAccountManagerWrapper className="wrapper">
      { isHeader && <Alerts /> }
      <StyledAccountManager data-tooltip-id="account_manager_name" ref={ ref }>
        <div className="preview">
          <Img
            src={ profilePicture }
            unloader={ <UserRound size={ 24 } /> }
            alt={ fullName }
          />
        </div>
        <div className="details">
          <p>
            {fullName}
          </p>
          <p className="title">{accountManagerLabel}</p>
        </div>
        { isHeader && (
          <>
            <StyledChevronUp
              onClick={ onToggle }
              $menuOpen={ menuOpen }
            />
            <DropdownMenu $open={ menuOpen }>
              <ul>
                <li>
                  <UserRound />
                  <span>{fullName}</span>
                  <Copy size={ 18 } className="copy" onClick={ () => copyCodeToClipboard(fullName, 'copy_account_name') } />
                </li>
                <li>
                  <AtSign />
                  <span className="email">{email}</span>
                  <Copy size={ 18 } className="copy" onClick={ () => copyCodeToClipboard(email, 'copy_email_address') } />
                </li>
                <li>
                  <Phone />
                  <span>{phone}</span>
                  <Copy size={ 18 } className="copy" onClick={ () => copyCodeToClipboard(phone, 'copy_phone_number') } />
                </li>
              </ul>
            </DropdownMenu>
          </>
        ) }
      </StyledAccountManager>
    </StyledAccountManagerWrapper>
  );
});

AccountManagerComponent.propTypes = {
  accountManager: shape({
    firstName: string.isRequired,
    lastName: string.isRequired,
    email: string.isRequired,
    phone: string.isRequired,
    profilePicture: string,
  }).isRequired,
  accountManagerLabel: string.isRequired,
  onToggle: func,
  menuOpen: bool,
  isHeader: bool,
};

export default AccountManagerComponent;
